import React from 'react';

import { Checkbox as AntdCheckbox } from 'antd';

import {
  ActionsRequisesDtoActionsRequisesEnum,
  AutocompleteUniteFOResultDtoFieldEnum,
  ReferentielControllerApi,
  TraitementResponseDtoClassementEnum,
  UniteFOResponseDto,
} from 'lib_api/lib/api/gen';

import { StatutTraitementFacturation } from 'hooks/dossiers/useSearchDossierSummaries';
import { useAsyncSearchCommuneEnlevement } from 'hooks/search/async/useAsyncSearchCommuneEnlevement';
import useAsyncSearchFourriere from 'hooks/search/async/useAsyncSearchFourriere';
import { useAsyncSearchUniteFo } from 'hooks/search/async/useAsyncSearchUniteFo';
import { actionsRequisesMapping } from 'hooks/useActionsRequises';
import { DashboardDSRFilter } from 'pages/delegue-securite-routiere/Dashboard/types';
import { DashboardFOFilter } from 'pages/forces-de-l-ordre/Dashboard/types';
import { FilterDashboardGardien } from 'pages/gardien-fourriere/Dashboard/DashboardHeader/Filters/types';
import { DashboardJusticeFilter } from 'pages/justice/Dashboard/types';
import { fetchBranchesList } from 'search/searchBranche';
import { fetchMotifSortie } from 'search/searchMotifSortie';

import { fetchStatutsConsultables } from '../search/searchStatutsDossier';
import {
  classementEnumMapping,
  statutEnumMapping,
  statutFacturationEnumMapping,
} from '../utils/enumData';

import BaseRangeDatePicker from './BaseForm/BaseRangeDatePicker';
import AsyncSelect from './BaseForm/Select/AsyncSelect';
import BaseSelect from './BaseForm/Select/BaseSelect';
import BaseSelectWithSearch from './BaseForm/Select/BaseSelectWithSearch';
import BaseSelectJuridiction from './BaseForm/Select/juridiction/BaseSelectJuridiction';
import BaseSelectMarqueMultiList from './BaseForm/Select/MultiList/BaseSelectMarqueMultiList';
import BaseSelectModeleMultiList from './BaseForm/Select/MultiList/BaseSelectModeleMultiList';
import { FormInputProps } from './BaseForm/types';

type keys =
  | 'statut'
  | 'classement'
  | 'fourriere'
  | 'commune'
  | 'actionsRequises'
  | 'includeDossiersAnonymises'
  | 'includeOnlyNotDetruitApresSorti'
  | 'natureMiseEnFourriere'
  | 'dateRangeMiseEnFourriere'
  | 'dateRangeOrdreDestruction'
  | 'dateRangeBonEnlevement'
  | 'dateRangeSortieDefinitive'
  | 'listUniteFo'
  | 'motifDeSortie'
  | 'marque'
  | 'modele'
  | 'statutsTraitementFacturation'
  | 'idJuridictionList'
  | 'dateRangeMiseAJour';

type AllFilters = DashboardJusticeFilter &
  DashboardFOFilter &
  DashboardDSRFilter &
  FilterDashboardGardien;

type FiltersSubFormsType = {
  [key in keys]: FormInputProps<Partial<AllFilters>, keys>;
};

function FiltersSubForms(
  refController: ReferentielControllerApi,
  adminActionsRequises?: ActionsRequisesDtoActionsRequisesEnum[],
): FiltersSubFormsType {
  const asyncSearchUniteFo = useAsyncSearchUniteFo(
    AutocompleteUniteFOResultDtoFieldEnum.ABREVIATION,
    true,
  );

  return {
    statut: {
      name: 'statut',
      label: 'Statut',
      render: () => (
        <AsyncSelect
          fetchOptions={() => {
            return fetchStatutsConsultables(refController);
          }}
          getOptionValue={option => option.toString()}
          getOptionLabel={option => statutEnumMapping[option].label}
          mode="multiple"
        />
      ),
    },
    classement: {
      name: 'classement',
      label: 'Classement',
      render: () => (
        <BaseSelect
          options={Object.values(TraitementResponseDtoClassementEnum)}
          getOptionValue={option => option.toString()}
          getOptionLabel={option => classementEnumMapping[option].label}
          mode="multiple"
        />
      ),
    },
    fourriere: {
      name: 'fourriere',
      label: 'Fourrière',
      render: () => (
        <BaseSelectWithSearch
          useSearchEntities={useAsyncSearchFourriere}
          getOptionValue={option => option.value}
          getOptionLabel={option => option.displayValue}
          mode="multiple"
          allowClear
        />
      ),
    },
    listUniteFo: {
      name: 'idUniteFOList',
      label: `Unités du périmètre`,
      render: () => (
        <BaseSelectWithSearch
          useSearchEntities={asyncSearchUniteFo}
          getOptionLabel={(unite: UniteFOResponseDto) => unite.abreviation}
          getOptionValue={(unite: UniteFOResponseDto) => unite.idCorrelation}
          mode="multiple"
          allowClear
        />
      ),
    },
    commune: {
      name: 'commune',
      label: "Commune d'enlèvement",
      render: () => (
        <BaseSelectWithSearch
          useSearchEntities={useAsyncSearchCommuneEnlevement}
          getOptionValue={option => option}
          getOptionLabel={option => option}
          mode="multiple"
          allowClear
        />
      ),
    },
    actionsRequises: {
      name: 'actionsRequises',
      label: 'Actions requises',
      render: () => (
        <BaseSelect
          options={adminActionsRequises ?? []}
          getOptionValue={option => option.toString()}
          getOptionLabel={option => actionsRequisesMapping[option]}
          mode="multiple"
          allowClear
          placeholder={
            !adminActionsRequises || adminActionsRequises.length === 0
              ? 'Aucune action requise disponible'
              : undefined
          }
          disabled={!adminActionsRequises || adminActionsRequises.length === 0}
        />
      ),
    },
    includeDossiersAnonymises: {
      name: 'includeDossiersAnonymises',
      label: 'Afficher les véhicules expirés :',
      valuePropName: 'checked',
      render: () => <AntdCheckbox />,
    },
    includeOnlyNotDetruitApresSorti: {
      name: 'includeOnlyNotDetruitApresSorti',
      label:
        'Afficher uniquement les véhicules sans date de destruction renseignée dans le SIV :',
      valuePropName: 'checked',
      render: () => <AntdCheckbox />,
    },
    natureMiseEnFourriere: {
      name: 'natureMiseEnFourriere',
      label: 'Nature de mise en fourrière',
      render: () => (
        <AsyncSelect
          fetchOptions={() => {
            return fetchBranchesList(refController);
          }}
          getOptionValue={option => option.id}
          getOptionLabel={option => option.nature}
          mode="multiple"
        />
      ),
    },
    dateRangeMiseEnFourriere: {
      name: 'dateRangeMiseEnFourriere',
      label: 'Date de mise en fourrière',
      render: () => <BaseRangeDatePicker allowEmpty={[true, true]} />,
    },
    dateRangeOrdreDestruction: {
      name: 'dateRangeOrdreDestruction',
      label: 'Date ordre de destruction',
      render: () => <BaseRangeDatePicker />,
    },
    dateRangeBonEnlevement: {
      name: 'dateRangeBonEnlevement',
      label: 'Date bon enlèvement',
      render: () => <BaseRangeDatePicker />,
    },
    dateRangeSortieDefinitive: {
      name: 'dateRangeSortieDefinitive',
      label: 'Date fin procédure',
      render: () => <BaseRangeDatePicker />,
    },
    motifDeSortie: {
      name: 'motifDeSortie',
      label: 'Motif de sortie',
      render: () => (
        <AsyncSelect
          fetchOptions={() => {
            return fetchMotifSortie(refController);
          }}
          getOptionValue={option => option.idCorrelation}
          getOptionLabel={option => option.motif}
          mode="multiple"
        />
      ),
    },
    marque: {
      label: 'Marque',
      name: 'idMarque',
      render: () => {
        return <BaseSelectMarqueMultiList withCorrelationId />;
      },
    },
    modele: {
      label: 'Modele',
      name: 'idModele',

      render: () => {
        return (
          <BaseSelectModeleMultiList
            idMarqueField={'idMarque'}
            withCorrelationId
          />
        );
      },
    },
    statutsTraitementFacturation: {
      name: 'statutsTraitementFacturation',
      label: 'Etat de facturation',
      render: () => (
        <BaseSelect
          options={Object.values(StatutTraitementFacturation)}
          getOptionValue={option => option.toString()}
          getOptionLabel={option => statutFacturationEnumMapping[option].label}
          mode="multiple"
          allowClear
        />
      ),
    },
    idJuridictionList: {
      name: 'idJuridictionList',
      label: 'Juridiction',
      render: () => <BaseSelectJuridiction mode="multiple" />,
    },
    dateRangeMiseAJour: {
      name: 'dateRangeMiseAJour',
      label: 'Date de dernière mise à jour',
      render: () => <BaseRangeDatePicker />,
    },
  };
}

export default FiltersSubForms;
